import React, { Fragment } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

import {
  EmploymentTypesEnum,
  EmployeeProbationTypesEnum,
  EmployeeContractTypesEnum,
} from '@thinknet-gmbh/platform-common';

import {
  Button,
  DatePickerInput,
  Form,
  Icon,
  ICONS,
  NumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
} from '@thinknet-gmbh/platform-common-ui';

import camelCase from 'lodash/camelCase';
import useToggle from 'src/hooks/useToggle';

import { DrawerComponent } from 'src/components/custom-drawer';
import { ContractForm } from './contract-form';
import { BooleanSelectInput } from 'src/pages/employee/components/boolean-select-input';

import { IWorkingConditionContract } from '../../employee.interfaces';

import { TitleRowLabel, StyledContainer, StyledRowContainer } from '../../employee-common.styles';
import {
  ContractListWrapper,
  GeneralConditionRowContainer,
  DeleteContainer,
  ItemsContainer,
} from '../working-conditions.styles';

const GeneralInformation = ({ isEdit, form }: { form: FormInstance; isEdit?: boolean }) => {
  const { t, i18n } = useTranslation(['common', 'employee', 'settings']);

  const { open, onClose, onOpen } = useToggle();

  const handleContractAdd = (contract: IWorkingConditionContract) => {
    const contracts = form.getFieldValue('contracts') || [];
    const lengthOfProbation = form.getFieldValue('lengthOfProbation') || 1;
    const probationType = form.getFieldValue('probationType') || 'months';
    form.setFieldsValue({
      contracts: [...contracts, contract],
      probationDate: moment(contract.hireDate).add(lengthOfProbation, probationType.toLowerCase()),
    });
    onClose();
  };

  const initialContracts = form.getFieldValue('contracts') || [];
  const lastContractDate = initialContracts[initialContracts.length - 1]?.hireDate || moment();

  return (
    <StyledContainer>
      <DrawerComponent
        title={t(`employee:workingConditions.buttons.extendContract`)}
        onClose={onClose}
        visible={open}
        destroyOnClose
      >
        <ContractForm onClose={onClose} onFinishHandler={handleContractAdd} />
      </DrawerComponent>
      <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('employee:workingConditions.labels.generalInformation')}
      </TitleRowLabel>
      <GeneralConditionRowContainer>
        <Form.Item
          name={'active'}
          label={t('employee:labels.activeWorkingCondition')}
          rules={[
            {
              required: true,
              message: t('common:validation.error.fieldRequired'),
            },
          ]}
        >
          <BooleanSelectInput t={t} />
        </Form.Item>
        <Form.Item
          name={'employmentType'}
          label={t('employee:labels.employmentType')}
          rules={[
            {
              required: true,
              message: t('employee:validationError.employmentTypeRequired'),
            },
          ]}
        >
          <SelectInput placeholder={t('employee:workingConditions.labels.internal')}>
            {Object.keys(EmploymentTypesEnum).map(type => (
              <SelectInput.Option key={type} value={type}>
                {t(`employee:workingConditions.labels.${type.toLowerCase()}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => {
            return (
              <Form.Item
                name={'lengthOfProbation'}
                rules={[
                  {
                    required: true,
                    message: t('common:validation.error.fieldRequired'),
                  },
                ]}
                label={t('employee:labels.lengthOfProbation')}
                getValueFromEvent={(value: number) => {
                  const probationType = getFieldValue('probationType') || 'months';
                  setFieldsValue({
                    probationDate: moment(lastContractDate).add(value, probationType.toLowerCase()),
                  });

                  return value;
                }}
              >
                <NumberInput
                  className={'suffix-selector-input'}
                  placeholder={90}
                  maxLength={9}
                  addonAfter={
                    <Form.Item
                      name="probationType"
                      noStyle
                      getValueFromEvent={(value: string) => {
                        const lengthOfProbation = getFieldValue('lengthOfProbation');
                        const probationType = value || 'months';
                        setFieldsValue({
                          probationDate: moment(lastContractDate).add(
                            lengthOfProbation,
                            probationType.toLowerCase()
                          ),
                        });
                        return value;
                      }}
                      rules={[
                        {
                          required: true,
                          message: t('common:validation.error.fieldRequired'),
                        },
                      ]}
                    >
                      <SelectInput className={'suffix-selector'} style={{ width: 140 }}>
                        {Object.keys(EmployeeProbationTypesEnum).map(type => {
                          return (
                            <SelectInput.Option key={type} value={type}>
                              {t(
                                `employee:workingConditions.labels.probationTypes.${type.toLowerCase()}`
                              )}
                            </SelectInput.Option>
                          );
                        })}
                      </SelectInput>
                    </Form.Item>
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          name={'probationDate'}
          label={t('employee:labels.probationDate')}
          rules={[
            {
              required: true,
              message: t('employee:validationError.probationDateRequired'),
            },
          ]}
        >
          <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
        </Form.Item>
      </GeneralConditionRowContainer>
      <StyledRowContainer>
        <Form.List name="contracts">
          {(fields, { remove }) => {
            return (
              <ContractListWrapper>
                {fields.map(field => (
                  <ItemsContainer key={field.key}>
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue, setFieldsValue }) => {
                        const type = getFieldValue(['contracts', field.name, 'contractType']);
                        return (
                          <Fragment>
                            <Form.Item
                              name={[field.name, 'contractType']}
                              label={t('employee:workingConditions.labels.contractType.title')}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'employee:workingConditions.validation.contractTypeRequired'
                                  ),
                                },
                              ]}
                            >
                              <SelectInput
                                disabled={!isEdit || type === EmployeeContractTypesEnum.NOT_FIXED}
                                placeholder={t('common:placeholders.selectFromList')}
                              >
                                {Object.keys(EmployeeContractTypesEnum).map(key => (
                                  <SelectInput.Option key={key} value={key}>
                                    {t(
                                      `employee:workingConditions.labels.contractType.${camelCase(
                                        key
                                      )}`
                                    )}
                                  </SelectInput.Option>
                                ))}
                              </SelectInput>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'hireDate']}
                              label={t('common:labels.hireDate')}
                              getValueFromEvent={(value: string) => {
                                const contracts = getFieldValue('contracts') || [];

                                if (contracts?.length === field.key + 1) {
                                  const lengthOfProbation = getFieldValue('lengthOfProbation') || 1;
                                  const probationType = getFieldValue('probationType') || 'months';
                                  setFieldsValue({
                                    probationDate: moment(value).add(
                                      lengthOfProbation,
                                      probationType.toLowerCase()
                                    ),
                                  });
                                }

                                return value;
                              }}
                              rules={[
                                {
                                  required: true,
                                  message: t('common:validation.error.fieldRequired'),
                                },
                              ]}
                            >
                              <DatePickerInput
                                inputReadOnly={true}
                                lang={i18n.language}
                                format="DD.MM.YYYY"
                              />
                            </Form.Item>
                          </Fragment>
                        );
                      }}
                    </Form.Item>

                    <DeleteContainer>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const contractType = getFieldValue([
                            'contracts',
                            field.name,
                            'contractType',
                          ]);
                          return contractType === EmployeeContractTypesEnum.FIXED ? (
                            <Form.Item
                              name={[field.name, 'endDate']}
                              label={t('employee:workingConditions.labels.endDate')}
                              rules={[
                                {
                                  required: true,
                                  message: t('common:validation.error.fieldRequired'),
                                },
                              ]}
                            >
                              <DatePickerInput
                                inputReadOnly={true}
                                lang={i18n.language}
                                format="DD.MM.YYYY"
                              />
                            </Form.Item>
                          ) : null;
                        }}
                      </Form.Item>

                      {fields.length > 1 && (
                        <Icon
                          className={'remove-icon'}
                          icon={ICONS.trash}
                          rightMargin={false}
                          color={PLATFORM_THEME_COLORS.tone.red}
                          size={22}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </DeleteContainer>
                  </ItemsContainer>
                ))}

                <Form.Item noStyle dependencies={['contracts']}>
                  {({ getFieldValue }) => {
                    const lastType = getFieldValue([
                      'contracts',
                      fields[fields.length - 1]?.name,
                      'contractType',
                    ]);
                    return isEdit && lastType === EmployeeContractTypesEnum.FIXED ? (
                      <Button
                        type="text"
                        icon={ICONS.plus}
                        iconColor={PLATFORM_THEME_COLORS.primary.blue}
                        onClick={onOpen}
                      >
                        {t('employee:workingConditions.buttons.extendContract')}
                      </Button>
                    ) : null;
                  }}
                </Form.Item>
              </ContractListWrapper>
            );
          }}
        </Form.List>
      </StyledRowContainer>
    </StyledContainer>
  );
};

export default GeneralInformation;
